import { useMemo, useState } from 'react';
import type { Column, TicketSummary, User } from '@vd/types';
import { Avatar, JiraChip, Tooltip, displayTicketTitle, isTitlePending, cn } from '@vd/ui';
import { Table2, Image as ImageIcon, MessageSquare, AlertTriangle, Bot, ChevronUp, ChevronDown, MoreHorizontal, History } from 'lucide-react';
import { useOpenCardSession } from '../ticket/cardSessionContext';
import { When } from '../../lib/When';
import { readLocalThumb } from '../annotations/localThumbs';
import { ColumnDot } from './ColumnDot';
import { BoardViewEmpty, type BoardFilters } from './BoardViewEmpty';
import styles from './TableView.module.css';

/**
 * High-density tabular view of tickets — the "see everything" mode for
 * boards with 50+ tickets where Board/Cards become noisy. Same data
 * shape and handlers as CardsView so the toolbar's filter/sort/search
 * controls work uniformly. Click a row to open the ticket; click the
 * status pill to change column; click the overflow to get the same
 * actions menu as the kanban cards.
 *
 * The column-name pill is the same statusPill style as KCard so users
 * see the column color + name without a separate legend.
 */

type SortKey = 'title' | 'column' | 'created' | 'comments';

export interface TableViewProps {
  tickets: TicketSummary[];
  columns: Column[];
  creatorsById: Map<string, User>;
  membersById: Map<string, User>;
  onOpenTicket: (t: TicketSummary) => void;
  onCopyLink: (t: TicketSummary) => void;
  onOpenSource: (t: TicketSummary) => void;
  onStatusClick?: (t: TicketSummary, a: HTMLElement) => void;
  onOverflowClick?: (t: TicketSummary, a: HTMLElement) => void;
  onAssignClick?: (t: TicketSummary, a: HTMLElement) => void;
  bulkSelection?: Set<string>;
  onBulkToggle?: (ticketId: string, next: boolean) => void;
  /** Tickets currently in flight on a bulk operation — rendered with a
   * dim+spinner so the user gets feedback during long-running batches. */
  movingTicketIds?: Set<string>;
  /**
   * When true, the table doesn't act as its own scroll container —
   * the wrap renders at content height and an ancestor handles
   * scrolling. The thead's `position: sticky` then resolves against
   * that ancestor, which is exactly what stacked tables on the
   * inbox page need (each project's header sticks at the top of the
   * page scroll until the next group enters and replaces it).
   */
  embedded?: boolean;
  /** Keyboard-focus model — see CardsView. The focused row gets a
   * subtle highlight; J/K from the parent moves the cursor; Enter
   * opens the focused ticket. */
  focusedTicketId?: string | null;
  onFocus?: (ticketId: string) => void;
  /** This view's screen name for pins, as on CardsView (TOADO-701). */
  screen?: string;
  /** The board's filters, for saying so when they hide every ticket (as on CardsView). */
  filters?: BoardFilters;
}

export function TableView({
  tickets,
  columns,
  creatorsById,
  membersById,
  bulkSelection,
  onBulkToggle,
  movingTicketIds,
  onOpenTicket,
  onStatusClick,
  onOverflowClick,
  onAssignClick,
  focusedTicketId,
  onFocus,
  embedded = false,
  screen,
  filters,
}: TableViewProps) {
  const openSession = useOpenCardSession();
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  // null sortKey means "use the order the parent already sorted by",
  // which respects the toolbar's prefs (Newest/Oldest/Most comments).
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const colById = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return tickets;
    const arr = [...tickets];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'title') cmp = a.title.localeCompare(b.title);
      else if (sortKey === 'column') {
        const an = colById.get(a.columnId)?.name ?? '';
        const bn = colById.get(b.columnId)?.name ?? '';
        cmp = an.localeCompare(bn);
      } else if (sortKey === 'created') cmp = a.createdAt.localeCompare(b.createdAt);
      else if (sortKey === 'comments') cmp = a.commentCount - b.commentCount;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [tickets, sortKey, sortDir, colById]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'created' ? 'desc' : 'asc'); }
  };

  if (tickets.length === 0) {
    return <BoardViewEmpty icon={<Table2 size={28} />} filters={filters} screen={screen} className={styles.empty} />;
  }

  // Show the checkbox column only when bulk-select is active OR a row is
  // hovered (the row CSS reveals the checkbox on :hover). Header always
  // renders the slot so column widths don't reflow when the user clicks
  // their first checkbox.
  const bulkActive = (bulkSelection?.size ?? 0) > 0;
  const allSelected = bulkActive && tickets.every((t) => bulkSelection?.has(t.id));
  const someSelected = bulkActive && !allSelected;
  const toggleAll = (next: boolean) => {
    if (!onBulkToggle) return;
    for (const t of tickets) {
      const current = bulkSelection?.has(t.id) ?? false;
      if (current !== next) onBulkToggle(t.id, next);
    }
  };

  return (
    <div className={embedded ? styles.wrapEmbedded : styles.wrap} data-toado-view={screen}>
      <table className={styles.table}>
        <colgroup>
          <col className={styles.checkCol} />
          <col className={styles.colThumb} />
          <col className={styles.colTitle} />
          <col className={styles.colDescription} />
          <col className={styles.colColumn} />
          <col className={styles.colSignals} />
          <col className={styles.colAssignees} />
          <col className={styles.colCommentCount} />
          <col className={styles.colCreated} />
          <col className={styles.colActions} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.checkCol}>
              <input
                type="checkbox"
                className={styles.checkAll}
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={(e) => toggleAll(e.target.checked)}
                aria-label={allSelected ? 'Deselect all rows' : 'Select all rows'}
                title={allSelected ? 'Deselect all' : 'Select all'}
              />
            </th>
            <th className={styles.colThumb} aria-label="Preview" />
            <SortableHeader label="Title" active={sortKey === 'title'} dir={sortDir} onClick={() => toggleSort('title')} />
            <th className={styles.colDescription}>Description</th>
            <SortableHeader label="Category" active={sortKey === 'column'} dir={sortDir} onClick={() => toggleSort('column')} className={styles.colColumn} />
            <th className={styles.colSignals} aria-label="Signals" />
            <th className={styles.colAssignees}>Assigned</th>
            <SortableHeader label="Comments" active={sortKey === 'comments'} dir={sortDir} onClick={() => toggleSort('comments')} className={styles.colCommentCount} />
            <SortableHeader label="Created" active={sortKey === 'created'} dir={sortDir} onClick={() => toggleSort('created')} className={styles.colCreated} />
            <th className={styles.colActions} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((t) => {
            const col = colById.get(t.columnId);
            const assignees = t.assignees.map((id) => membersById.get(id)).filter(Boolean) as User[];
            const creator = creatorsById.get(t.createdBy);
            const partial = t.health !== 'complete';
            const isBulk = bulkSelection?.has(t.id) ?? false;
            const isMoving = movingTicketIds?.has(t.id) ?? false;
            const thumbUrl = t.thumbnailUrl ?? readLocalThumb(t.id);
            const preview = t.descriptionPreview;
            return (
              <tr
                key={t.id}
                data-ticket-id={t.id}
                className={[
                  isBulk && styles.rowSelected,
                  isMoving && styles.rowMoving,
                  // Focus highlight is a separate visual layer from
                  // bulk selection so j/k cursor + ticked checkbox can
                  // coexist on the same row without color conflict.
                  focusedTicketId === t.id && !isBulk && styles.rowFocused,
                ].filter(Boolean).join(' ') || undefined}
                aria-busy={isMoving || undefined}
                onClick={(e) => {
                  // Ignore clicks that started on an interactive descendant
                  // (status pill, overflow, assignee picker, checkbox) —
                  // those have their own handlers.
                  const tgt = e.target as HTMLElement;
                  if (tgt.closest('button, input, [role="button"]')) return;
                  // Click sets focus first, then opens. Reading the
                  // updated focus on next render is fine; the open
                  // navigation is what the user wanted.
                  onFocus?.(t.id);
                  onOpenTicket(t);
                }}
              >
                <td className={styles.checkCol} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className={bulkActive ? styles.checkAlways : styles.checkHoverOnly}
                    checked={isBulk}
                    onChange={(e) => onBulkToggle?.(t.id, e.target.checked)}
                    aria-label={`Select ${displayTicketTitle(t)}`}
                  />
                </td>
                <td className={styles.thumbCell}>
                  {thumbUrl ? (
                    <img src={thumbUrl} alt="" className={styles.thumb} loading="lazy" />
                  ) : (
                    <span className={styles.thumbFallback} aria-hidden="true">
                      <ImageIcon size={12} />
                    </span>
                  )}
                </td>
                <td className={styles.titleCell}>
                  <span className={styles.titleLine}>
                    <Tooltip content={displayTicketTitle(t)} placement="top">
                      <span className={cn(styles.title, isTitlePending(t) && styles.titlePending)}>
                        {displayTicketTitle(t)}
                      </span>
                    </Tooltip>
                    <JiraChip jira={t.jira} />
                  </span>
                </td>
                <td className={styles.descriptionCell}>
                  {preview ? (
                    <Tooltip content={preview} placement="top" multiline>
                      <span className={styles.preview}>{preview}</span>
                    </Tooltip>
                  ) : (
                    <span className={styles.previewDash}>-</span>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className={styles.statusPill}
                    onClick={(e) => onStatusClick?.(t, e.currentTarget)}
                    title={`Move from ${col?.name ?? 'unknown'}`}
                  >
                    <ColumnDot tone={col?.tone} size={6} />
                    <span className={styles.statusName}>{col?.name ?? '-'}</span>
                  </button>
                </td>
                <td>
                  {/* Inner wrapper holds the flex layout so the <td>
                      keeps its native display:table-cell — applying
                      display:flex directly to a td breaks the table
                      row layout (the row's bottom border drops out
                      of alignment with neighboring cells). */}
                  <div className={styles.signals}>
                    {partial && (
                      <Tooltip content={t.health === 'failed' ? 'Capture failed' : 'Capture partial'}>
                        <AlertTriangle size={12} className={styles.iconWarn} />
                      </Tooltip>
                    )}
                    {t.hasAiActivity && (
                      <Tooltip content="AI activity"><Bot size={12} className={styles.iconAi} /></Tooltip>
                    )}
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className={styles.assignees}
                    onClick={(e) => onAssignClick?.(t, e.currentTarget)}
                  >
                    {assignees.length === 0 ? (
                      <span className={styles.assignDash}>-</span>
                    ) : (
                      assignees.slice(0, 3).map((u) => (
                        <Tooltip key={u.id} content={u.name}><Avatar name={u.name} size="xs" /></Tooltip>
                      ))
                    )}
                    {assignees.length > 3 && <span className={styles.assignMore}>+{assignees.length - 3}</span>}
                  </button>
                </td>
                <td className={styles.commentsCell}>
                  {t.commentCount > 0 && (
                    <span className={styles.commentChip}>
                      <MessageSquare size={11} />
                      {t.commentCount}
                    </span>
                  )}
                </td>
                <td className={styles.createdCell} title={creator?.name ?? t.createdBy}>
                  <When at={t.createdAt} exact />
                </td>
                <td className={styles.actionsCell}>
                  {/* The visit before the report, over the table (TOADO-945): on hover, left of More. */}
                  {openSession && t.hasSession && (
                    <Tooltip content="What they did before reporting">
                      <button
                        type="button"
                        className={cn(styles.overflow, styles.sessionBtn)}
                        aria-label="Session"
                        data-testid="row-session"
                        onClick={(e) => { e.stopPropagation(); openSession(t, e.currentTarget); }}
                      >
                        <History size={14} />
                      </button>
                    </Tooltip>
                  )}
                  <button
                    type="button"
                    className={styles.overflow}
                    aria-label="More"
                    onClick={(e) => onOverflowClick?.(t, e.currentTarget)}
                  >
                    <MoreHorizontal size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface SortableHeaderProps {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  className?: string;
}

function SortableHeader({ label, active, dir, onClick, className }: SortableHeaderProps) {
  return (
    <th className={className}>
      <button type="button" className={styles.sortBtn} onClick={onClick} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
        {label}
        {active && (dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </button>
    </th>
  );
}
